import type { ParsedQs } from 'qs';
import type { Attribution, MarketingData } from './marketing';
import type { RequestUser } from './request';
import type { Obligation } from './ztb';

import type { BiztechMarketingContext, Content, NewAccountInfoWithPassword } from '.';
import type { Currency } from './currency';

type OfferDetail = {
  accordionHeaderText: string;
  offerInfo?: Array<string>;
  features?: Array<string>;
  showOfferName?: boolean;
  openAccordion?: boolean; // Set this if the accordion should be open by default.
};

type OfferDetails = Array<OfferDetail>;

export type OfferItem = {
  name?: string;
  price: number;
  productCode?: string;
  subtext?: string;
};

type ThankYouContent = {
  customHeadingText?: string;
  middleTextContent?: string;
  resourceHeading?: string;
  resourceContent?: Array<Content>;
  topTextContent?: Array<string>;
  icon?: string; // skylab brand icons only name of icon
};

export type PlanIncludes = {
  heading: string;
  items: Array<string>;
};

export type OfferOrderSummary = {
  additionalItems?: Array<OfferItem>;
  avataxRequired?: boolean;
  heading?: string;
  subheading?: string;
  planIncludes?: PlanIncludes;
  secondaryHeading?: string;
  secondaryItems?: Array<OfferItem>;
  showBillingTermsInOrderSummary?: boolean;
  showDiscountedSubTotal?: boolean;
  totalSubtext?: Array<string>;
  totalText?: string;
};

export type OfferFlow =
  | null // TODO: look into removing null
  | 'add-integration'
  | 'avalara-licensing'
  | 'avatax-sandbox'
  | 'dbna'
  | 'elr'
  | 'exemptions'
  | 'extractor'
  | 'free-trial'
  | 'ioss'
  | 'license-guidance'
  | 'license-preparation'
  | 'risk-assessment';

type QueryName = string;
export type OfferSpecificQueryParams = {
  [StateName: string]: QueryName;
};

export type AllowedUsers = 'EXISTING' | 'NEW';
export type UsesConnectors = 'OPTIONAL' | 'REQUIRED';

export type CommonOfferDetails = {
  adjustableQuantity?: boolean;
  allowedUsers?: AllowedUsers;
  attributionDefaults: Attribution;
  initializeNewAccounts?: boolean;
  isEmeaOffer?: boolean;
  isFreeTrial?: boolean;
  isPaymentRequired: boolean;
  minQuantity?: number;
  maxQuantity?: number;
  obligationsRequiredForPlans?: boolean;
  offerName: string;
  offerSpecificQueryParams?: OfferSpecificQueryParams;
  taxCode?: string;
  usesConnectors?: UsesConnectors;
};

type TermsAndConditionsLink = {
  text: string;
  url: string;
};

type EsbPlanClientDetails = { featuresList: Array<string> };
export type ExemptionsPlanClientDetails = { certificatesIncluded: string };
export type IossPlanClientDetails = { currency: Currency };
type OfferSpecificPlanClientDetails =
  | EsbPlanClientDetails
  | ExemptionsPlanClientDetails
  | IossPlanClientDetails
  | object;

export type OfferPlanClientDetails = OfferSpecificPlanClientDetails & {
  campaignId?: string;
  currency?: Currency;
  isSubscribedToPlan?: boolean;
  name: string;
  orderSummaryOverrides?: Partial<OfferOrderSummary>;
  planCountry?: string;
  planSpecificBillingTerms?: Array<string>;
  // Kyle - if we end up having pricing being calculated for offers sold via API we
  // need to make sure it is consistently done from client to server via productItems
  productItems?: Array<OfferItem>;
  tier?: number;
};

type AddItem = { productCode: string };

export type OfferSpecificSettings = {
  [setting: string]: string | null;
};

export type AddOfferRequest = {
  // TODO (Andrew) - when necessary, we should be able to make connectorId part
  //                 of the validatedOfferSpecificSettings. We should also be
  //                 able to do the same for tierNumber, although that may take
  //                 a bit more finagling, since the properties are only typed
  //                 to allow strings or null values. In fact, we should
  //                 make all the optional params part of the offer specific
  //                 settings, since that will address a FlowFixMe
  accountId?: string;
  connectorId?: string;
  customerAccountId?: string;
  discountPercent?: number;
  isPaymentRequired: boolean;
  marketingContext: {
    opportunity: BiztechMarketingContext;
  };
  offerCode: string;
  paymentMethodId?: string | null;
  quantity?: number;
  salesRepId: string;
  shouldUseUpgradeApi?: boolean;
  tierNumber?: number;
  upgradeSubtype?: string;
  validatedOfferSpecificSettings?: OfferSpecificSettings;
};

export type OfferPlan = OfferPlanClientDetails & {
  addItems?: Array<AddItem>;
  checkEligibility?: (obligations: Array<Obligation>) => boolean;
  emailDetails?: EmailDetails;
  obligationFeatureNameRegex?: RegExp;
  offerCode: string;
  planSpecificOfferParams?: Partial<AddOfferRequest>;
  upgradeSubtype?: string;
};

export enum Pages {
  Plans = 'plans',
  GetStarted = 'get-started',
  Company = 'company',
  Payment = 'payment',
  ReviewOrder = 'review-order',
  Summary = 'summary',
  SettingUp = 'setting-up',
  ThankYou = 'thank-you',
  Redirect = 'redirect',
  SelectConnector = 'select-connector',
  AddBillingContact = 'add-billing-contact',
}

export type OfferClientDetails = CommonOfferDetails & {
  allowEmbeddedView?: boolean;
  analyticsProductCategory: string;
  buyButtonTextOverride?: string;
  deliverySystemToPollFor?: string;
  offerDetails?: OfferDetails;
  offerFlow: OfferFlow;
  orderSummary: OfferOrderSummary;
  pages: Array<Pages>;
  redirectUrl?: string;
  reviewOrder?: boolean;
  showEmbeddedSideContent?: boolean;
  // if no termsAndConditions set, general terms and conditions will be used
  termsAndConditions?: Array<TermsAndConditionsLink>;
  thankYouContent: ThankYouContent;
};

export type ClientOfferWithPlans = OfferClientDetails & {
  plans: Array<OfferPlanClientDetails>;
};

export type EmailDetails = {
  renewalDate?: string;
  button?: string;
  additionalText?: string;
  primaryContactEmail?: string;
};

export type CheckIsValidConnectorParams = {
  connectorId: string;
  obligations: Array<Obligation>;
  user?: RequestUser;
};

type EmailTemplateName =
  // TODO: replace licensing email with the below after AAA-7334 is resolved
  // np'sales-tax-registration-purchase-confirmation'
  'avalara-licensing-purchase-confirmation' | 'dynamic-shared-email';

export type OfferServerDetails = CommonOfferDetails & {
  checkIsValidConnector?: (_: CheckIsValidConnectorParams) => Promise<boolean>;
  defaultPlanName?: string;
  emailTemplateName?: EmailTemplateName;
  magicLinkRedirectUrl?: string;
  skipConfirmationEmail?: boolean;
  snsTag?: string;
};

export type ServerOfferWithPlans = OfferServerDetails & {
  shouldUseUpgradeApi: boolean;
  plans: Array<OfferPlan>;
};

export type GetClientDetailsParams = {
  isEmbedded: boolean;
  isLoggedIn: boolean;
  hasSelectedConnector: boolean;
  avataxAccountId?: string;
};

export type GetPlansParams = {
  obligations?: Array<Obligation> | null;
  query?: ParsedQs;
};

export type OfferDefinition = {
  readonly featureFlag?: string;
  readonly offerFlow: OfferFlow;
  checkUpgradeApiEligibility?: (obligations: Array<Obligation>) => boolean;
  getPlans: (params: GetPlansParams) => Promise<Array<OfferPlan>>;
  // NOTE (Andrew) - if getClientDetails is missing, this indicates an API-only offer with no
  //                 corresponding Buy UI:
  getClientDetails?: (arg0: GetClientDetailsParams) => Promise<OfferClientDetails>;
  getServerDetails: () => OfferServerDetails;
};

export type OfferPurchaseRequest = {
  discountCode?: string | null;
  marketingData?: MarketingData;
  offerFlow: OfferFlow;
  offerSpecificSettings?: OfferSpecificSettings;
  paymentMethodId?: string;
  quantity?: number;
  selectedPlanName?: string;
  totalCost?: string;
  connectorId?: string;
  salesTax?: number | null;
};

export type CreateAccountForOfferRequest = OfferPurchaseRequest & {
  accountInfo: NewAccountInfoWithPassword;
  retryKey?: string;
};

export type DiscountConfig = {
  discountPercentage: number | undefined;
  additionalLegalText: string | null;
  discountName: string | null;
};

export type Offer = {
  offerCode: string;
  createdDate: string;
  correlationId: string;
  connectorId: string;
  dealinfoId: string;
  opportunityId: string;
  quoteId: string;
  orderId: string;
  orderProcessingStatus: 'Pending' | 'Success';
  contractId: string;
  contractStatus: string;
  fasterProvisioningSent: boolean;
};

export type SoldOffers = {
  customerAccountId: string;
  offers: Array<Offer>;
  success: boolean;
  errorMessage?: string;
};

export type SoldOffersResponse = {
  count: number;
  value: Array<SoldOffers>;
};
